import {
  ShowWithComedian,
  filterOutPasswordProtectedShowsServer,
  updateTixologiAvailabilityServer,
} from "../../legacy";
import type { VenuePageTimezones } from "../../models/venue_pages";
import dayjs from "@punchup/utils/dayjs";

/**
 * A show leaves the public venue-page carousel this many hours after its
 * (venue-local) start time. Short by design: the hero carousel advertises
 * *upcoming* shows, so a show is dropped ~1h after it begins rather than
 * lingering until the next morning (PUN-5015). Every venue-page show surface
 * (getVenuePageData, prefetchVenuePageShows, /api/shows, and the grouped grid's
 * getGroupedShowsForVenuePage) reads it via {@link publicShowStartCutoff}.
 */
export const PUBLIC_SHOW_GRACE_HOURS = 1;

/**
 * Lower bound for a venue page's still-showing shows: `now − graceHours`
 * expressed in the venue's LOCAL wall clock, as a naive `YYYY-MM-DDTHH:mm:ss`
 * string with no offset — so it lines up with `comedian_shows.datetime`, a
 * `timestamp without time zone` that stores venue-local digits.
 *
 * Rendered with `.format(...)`, never `.toISOString()`: UTC rendering would
 * skew the boundary by the venue's offset (~4-5h for US venues), which at a 1h
 * grace is large enough to HIDE not-yet-started shows. Mirrors the grid's
 * `upcomingShowsCutoff` (venues) — same naive-wall-clock contract, anchored at
 * `now − grace` instead of start-of-today:
 * - **earliest across locations** so a westmost in-grace show is never hidden
 *   when an eastern clock is already later;
 * - **no timezone data → the pre-existing rolling now-12h UTC lookback**, NOT
 *   the 1h grace. "Local now" is unknowable without a tz, and a 1h UTC cutoff
 *   compared against the naive local column would hide a US venue's still-
 *   upcoming evening shows. 12h keeps this branch's behavior exactly as it was
 *   before PUN-5015 (the old hand-rolled `now-12h` default) — it errs toward
 *   staleness, never toward hiding a future show. Only tz-having venues get the
 *   tighter 1h window.
 */
export function publicShowStartCutoff(
  venueTimezones: VenuePageTimezones,
  graceHours: number = PUBLIC_SHOW_GRACE_HOURS
): string {
  if (!venueTimezones?.length) {
    return dayjs().subtract(12, "hours").toISOString();
  }
  // Identical fixed-width formats ⇒ lexicographic min = chronological min of
  // the wall-clock digits, which is the right axis for a tz-naive column.
  return venueTimezones
    .map((entry) =>
      dayjs()
        .tz(entry.timezone)
        .subtract(graceHours, "hours")
        .format("YYYY-MM-DDTHH:mm:ss")
    )
    .sort()[0];
}

/**
 * The pipeline every PUBLIC surface must apply to shows before display:
 * 1. drop shows started more than PUBLIC_SHOW_GRACE_HOURS ago in the venue's
 *    LOCAL time (or with no valid datetime — the default paths'
 *    `gte("datetime", …)` queries exclude those too). Pass `venueTimezones`
 *    so the cutoff is venue-local; without it the comparison falls back to the
 *    lenient now-12h window rather than risk hiding a not-yet-started show —
 *    see {@link publicShowStartCutoff},
 * 2. OR live Tixologi availability into `is_sold_out` (the Supabase column
 *    alone is only the manual force-sold-out lever) and drop inactive
 *    (admin-only) events — both from the same response, see
 *    updateTixologiAvailabilityServer,
 * 3. drop password-protected/hidden Tixologi events.
 *
 * Both Tixologi calls are batched (one POST each for the whole set) and fail
 * open, so an outage degrades to the raw rows rather than an empty surface.
 */
export async function applyPublicShowPipeline(
  shows: ShowWithComedian[],
  venueTimezones: VenuePageTimezones = []
): Promise<ShowWithComedian[]> {
  const cutoff = dayjs(publicShowStartCutoff(venueTimezones));
  const upcomingShows = shows.filter((show) => {
    const showDatetime = dayjs(show.datetime);
    return showDatetime.isValid() && !showDatetime.isBefore(cutoff);
  });

  if (upcomingShows.length === 0) {
    return [];
  }

  const [showsWithAvailability, visibleShows] = await Promise.all([
    updateTixologiAvailabilityServer(upcomingShows),
    filterOutPasswordProtectedShowsServer(upcomingShows),
  ]);
  const visibleShowIds = new Set(visibleShows.map((show) => show.id));

  return showsWithAvailability.filter((show) => visibleShowIds.has(show.id));
}
